import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface AuditRow {
  readonly aiAuditLogId: string;
  readonly eventType: string;
  readonly entityType: string;
  readonly entityId: string | null;
  readonly entityCode: string | null;
  readonly actorId: string | null;
  readonly action: string | null;
  readonly ipAddress: string | null;
  readonly eventTimestamp: string;
}

const EVENT_TYPES = [
  'PROMPT_SUBMITTED', 'RESPONSE_GENERATED', 'RESPONSE_APPROVED', 'KNOWLEDGE_QUERIED', 'SUMMARY_GENERATED',
  'AI_POLICY_UPDATED', 'ACCESS_GRANTED', 'ACCESS_REVOKED', 'MODEL_CREATED', 'PROMPT_CREATED',
  'REQUEST_BLOCKED', 'HALLUCINATION_FLAGGED', 'RISK_REGISTERED', 'ANALYTICS_COMPUTED',
];
const PAGE = 25;

/**
 * M38 — AI Audit Dashboard (§20/§26). Read-only view of the append-only AI audit
 * trail (T513) that records every governed AI event — prompt submission, response
 * generation + approval, knowledge queries, summary generation, policy changes and
 * data-protection blocks — with actor, entity, action, source IP and timestamp
 * (BR-03/BR-07). Filterable by event type and entity type. All routes require
 * AUDIT_VIEW; tenant isolation is enforced server-side (LAW 4).
 */
export function AiAuditDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [total, setTotal] = useState(0);
  const [eventType, setEventType] = useState('');
  const [entityType, setEntityType] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (eventType) params.set('eventType', eventType);
    if (entityType) params.set('entityType', entityType);
    params.set('limit', String(PAGE));
    params.set('offset', String(page * PAGE));
    apiRequest<{ data: AuditRow[]; total: number }>(`/v1/ai/audit?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, eventType, entityType, page]);

  return (
    <main aria-label="ai-audit-dashboard">
      <h1>{t('m38.audit.title')}</h1>

      <section aria-label="audit-filters">
        <label>{t('m38.audit.eventType')}
          <select aria-label="filter-event-type" value={eventType} onChange={(e) => { setEventType(e.target.value); setPage(0); }}>
            <option value="">{t('m38.audit.allEvents')}</option>
            {EVENT_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
          </select>
        </label>
        <label>{t('m38.audit.entityType')}
          <input aria-label="filter-entity-type" value={entityType} onChange={(e) => { setEntityType(e.target.value); setPage(0); }} placeholder={t('m38.audit.entityTypePh')} />
        </label>
      </section>

      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="audit-total">{t('m38.audit.total', { count: total })}</p>
          <DataTable
            ariaLabel="audit-table"
            rows={rows}
            rowKey={(r) => r.aiAuditLogId}
            emptyMessage={t('m38.audit.noneMatch')}
            columns={[
              { key: 'eventType', header: t('m38.audit.colEvent'), render: (r) => r.eventType },
              { key: 'entityType', header: t('m38.audit.colEntityType'), render: (r) => r.entityType },
              { key: 'entityCode', header: t('m38.audit.colEntityCode'), render: (r) => r.entityCode ?? '—' },
              { key: 'action', header: t('m38.common.action'), render: (r) => r.action ?? '—' },
              { key: 'actorId', header: t('m38.audit.colActor'), render: (r) => r.actorId ?? '—' },
              { key: 'ipAddress', header: t('m38.audit.colIp'), render: (r) => r.ipAddress ?? '—' },
              { key: 'eventTimestamp', header: t('m38.audit.colTime'), render: (r) => at(r.eventTimestamp) },
            ]}
          />

          <nav aria-label="audit-pagination">
            <Button variant="secondary" type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('m38.common.previous')}</Button>
            <span aria-label="page-indicator">{t('m38.common.pageOf', { page: page + 1, pages: Math.max(1, Math.ceil(total / PAGE)) })}</span>
            <Button variant="secondary" type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>{t('m38.common.next')}</Button>
          </nav>
        </>
      )}
    </main>
  );
}
